<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { usePaymentsStore } from '@/store/modules/payments';
import { useTicketProduct } from '@/composables/saju-insight/useTicketProduct';
import { formatNumber } from '@/composables/functions';
import ConfirmComponent from '@/components/common/Confirm.vue';
import { addContentLogAPI } from '@/api/logServiceApi';

/**
 * 낭만작명 결제 방법 선택 팝업 (PC/Mobile 공용).
 * 부모(saju-naming)가 분석권 보유 시 팝업을 건너뛰므로, 이 팝업이 뜨는 시점엔 분석권 항상 0건 →
 * 우측 박스는 결제 옵션이 아니라 할인 분석권 업셀 카드(상품 상세로 이동).
 */
const props = defineProps({
  isVisible: { type: Boolean, default: false },
  analysisType: { type: Object, default: null },
  ticketCount: { type: Number, default: 0 },
  isPointActive: { type: Boolean, default: false },
  isTicketActive: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'select-method']);

const router = useRouter();
const paymentsStore = usePaymentsStore();
const { fetchTicketProductByTypeId } = useTicketProduct();

const pointPrice = computed(() => props.analysisType?.pointPrice ?? 0);
const pointBasePrice = computed(() => props.analysisType?.pointBasePrice ?? 0);
/** 포인트 직접 구매가가 정가 대비 할인되어 있는지 */
const isPointDiscounted = computed(
  () => Number(pointBasePrice.value) > Number(pointPrice.value),
);
const ticketDisplayText = computed(() =>
  props.ticketCount > 0 ? `${props.ticketCount}건` : '0건',
);

/** 분석권 업셀 카드용 상품 정보 ({ boardId, articleId, perUnitPrice, unit, discountPct }) */
const ticketProduct = ref(null);

const buyType = ref('');

/** 업셀 조회 세대 + in-flight promise — 팝업을 닫고 다른 모드로 다시 열면 먼저 떠난 이전 모드의
 *  늦은 응답이 카드를 덮어쓸 수 있어(다른 모드 상품으로 이동 유도) 마지막 조회만 반영한다. */
let upsellSeq = 0;
let upsellPromise = null;

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      // 팝업이 열릴 때 기본 선택: 포인트 활성 우선, 아니면 분석권
      if (props.isPointActive) buyType.value = 'point';
      else if (props.isTicketActive) buyType.value = 'ticket';
      else buyType.value = '';
      // 분석권 업셀 상품을 비동기로 조회 (팝업 표시 지연 안 시킴)
      // 서버가 옵션별로 지정한 추천 상품(recommendedProductId) 우선, 없으면 최저 단가 상품
      ticketProduct.value = null;
      const seq = ++upsellSeq;
      upsellPromise = fetchTicketProductByTypeId(props.analysisType?.analysisTypeId, {
        recommendedProductId: props.analysisType?.recommendedProductId ?? null,
        modeKey: props.analysisType?.modeKey ?? null,
      }).then((product) => {
        if (seq === upsellSeq) ticketProduct.value = product;
        return product;
      });
    }
  },
);

const handleBuyPoint = () => {
  if (!props.isPointActive) return;
  addContentLogAPI({ contentType: 'ap_naming_payment_opt3', option3: '포인트' });
  buyType.value = 'point';
};

/** 분석권 구매 이동 확인 팝업 ref */
const ticketConfirmRef = ref(null);

/** 업셀 카드 클릭 → 바로 이동하지 않고 작성 내용 초기화 안내 확인 팝업 표시 */
const goTicketProduct = () => {
  addContentLogAPI({ contentType: 'ap_naming_payment_opt3', option3: '분석권상품' });
  ticketConfirmRef.value?.openConfirmDialog();
};

/** 확인(네) 클릭 → 해당 분석권 상품 상세로 이동 (상품 못 찾으면 분석권 목록) */
const moveToTicketProduct = async () => {
  // 조회가 끝나기 전에 클릭한 경우 결과를 기다린다 — 게시판 미노출 추천 상품은 분석권
  // 목록에 보이지 않으므로, 목록으로 보내면 상품을 찾을 수 없는 막다른 화면이 된다
  if (!ticketProduct.value && upsellPromise) {
    try { await upsellPromise; } catch { /* 조회 실패 시 아래 목록 폴백 */ }
  }
  if (ticketProduct.value?.articleId) {
    router.push(
      `/purchase/ticket-detail/${ticketProduct.value.boardId}/${ticketProduct.value.articleId}`,
    );
  } else {
    router.push('/purchase/tickets');
  }
  emit('close');
};

const goConfirm = () => {
  if (!buyType.value) return;
  addContentLogAPI({ contentType: 'ap_naming_payment_opt3', option3: '확인' });
  emit('select-method', buyType.value);
};

const close = () => {
  addContentLogAPI({ contentType: 'ap_naming_payment_opt3', option3: '닫기' });
  emit('close');
};
</script>

<template>
  <Teleport to="body">
    <div v-if="isVisible" class="pop-analysis-info-backdrop">
      <div class="pop-analysis-info">
        <div class="pop-header">
          <div class="pop-title">사용하실 결제 방법을 선택해주세요!</div>
          <button class="pop-close" aria-label="닫기" @click="close" />
        </div>
        <div class="pop-info-row">
          <span class="pop-info-label">보유중</span>
          <div class="pop-info-point-row">
            <span class="pop-info-point">{{ paymentsStore?.formattedPoints }} P</span>
            <span class="pop-info-divider">|</span>
            <div class="pop-info-ticket">
              <span>작명분석권</span>
              <span>{{ ticketDisplayText }}</span>
            </div>
          </div>
        </div>
        <div class="pop-options-row">
          <div
            class="option-box point"
            :class="{ active: buyType === 'point', disabled: !isPointActive }"
            @click="handleBuyPoint"
          >
            <div class="option-icon-n"/>
            <div class="option-desc-col">
              <div class="option-title">포인트 사용하기</div>
              <div class="option-value">
                <template v-if="isPointDiscounted">
                  <span class="option-base-price">{{ formatNumber(pointBasePrice) }} P</span>
                  <span class="option-price-arrow">→</span>
                  <span class="option-price-sale">{{ formatNumber(pointPrice) }} P</span>
                </template>
                <template v-else>{{ formatNumber(pointPrice) }} P</template>
              </div>
            </div>
          </div>
          <div class="option-box ticket-upsell" @click="goTicketProduct">
            <span
              v-if="ticketProduct?.perUnitPrice != null && ticketProduct.discountPct > 0"
              class="upsell-badge"
              >{{ ticketProduct.discountPct }}% 할인</span
            >
            <div class="option-icon-ticket"/>
            <div class="option-desc-col">
              <div class="option-title">할인 분석권</div>
              <div v-if="ticketProduct?.perUnitPrice != null" class="option-value">
                1{{ ticketProduct.unit }}당 {{ formatNumber(ticketProduct.perUnitPrice) }} P
              </div>
              <div v-else class="option-value upsell-fallback">상품 보기</div>
            </div>
            <span class="upsell-arrow" aria-hidden="true">›</span>
          </div>
        </div>
        <button
          class="paywall_use_button_confirm"
          :class="{ disabled: !buyType }"
          @click="goConfirm"
        >
          확인
        </button>
      </div>
      <!-- backdrop(z-index 9999) 내부에 둬야 같은 스태킹 컨텍스트에서 결제 팝업 위로 표시됨 -->
      <ConfirmComponent
        ref="ticketConfirmRef"
        title="할인 분석권 구매 화면으로 이동하면 현재 작성 중인 내용이 초기화될 수 있습니다."
        title2="이동하시겠습니까?"
        title2-class="confirm-highlight-orange"
        text="네"
        cancel-text="아니오"
        :next-url="moveToTicketProduct"
      />
    </div>
  </Teleport>
</template>

<style lang="scss">
/* 낭만궁합 결제 팝업과 동일 스타일 — 다른 페이지에서 재사용 가능하도록 non-scoped */
.pop-analysis-info-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pop-analysis-info {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  width: calc(100% - 32px);
  max-width: 400px;
  padding: 30px 24px 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pop-header {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 18px;

  .pop-close {
    position: absolute;
    right: 16px;
    top: 30px;
  }
}

.pop-title {
  color: var(--Gray-800, #333);
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.pop-close {
  background: url('@manseImages/icon_close_1.svg');
  background-size: 24px 24px;
  border: none;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.pop-info-row {
  display: flex;
  padding: 12px 20px;
  align-items: center;
  align-self: stretch;
  background: var(--Gray-200, #f8f8f8);
  color: var(--Gray-700, #575757);
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  margin-bottom: 12px;
  position: relative;
}

.pop-info-label {
  color: var(--orange-300-main, #eb4c10);
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.pop-info-point-row {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
}

.pop-info-point {
  flex: 1;
  text-align: right;
}

.pop-info-divider {
  color: #d9d9d9;
  margin: 0 12px;
}

.pop-info-ticket {
  flex: 1;
  display: flex;
  justify-content: space-between;
}

.pop-options-row {
  width: 100%;
  display: flex;
  gap: 12px;
  margin-bottom: 28px;
}

.option-box {
  flex: 1;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0 16px;
  color: var(--Gray-800, #333);
  gap: 12px;
  cursor: pointer;
  box-sizing: border-box;

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &.active {
    border: 2px solid var(--orange-300-main, #eb4c10) !important;
    background: var(--Orange-100, #fff8f5);
    margin: -1px;
    color: var(--orange-300-main, #eb4c10);
  }
}

.option-icon-n {
  width: 40px;
  height: 40px;
  background: url('@manseImages/icon_an_guide_point.png') no-repeat center/cover;
  margin-bottom: 4px;
}

.option-icon-ticket {
  width: 40px;
  height: 40px;
  background: url('@manseImages/icon_an_guide_Frame.png') no-repeat center/cover;
  margin-bottom: 4px;
}

.option-desc-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.option-title {
  color: var(--Gray-800, #333);
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
}

.option-value {
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  white-space: nowrap;
}

.option-base-price {
  color: var(--Gray-600, #757575);
  font-size: 13px;
  font-weight: 600;
  text-decoration-line: line-through;
}
.option-price-arrow {
  margin: 0 4px;
  color: var(--Gray-500, #949494);
  font-size: 13px;
  font-weight: 600;
}
.option-price-sale {
  color: var(--orange-300-main, #eb4c10);
  font-weight: 700;
}

.option-box.ticket-upsell {
  position: relative;
}
.upsell-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--orange-300-main, #eb4c10);
  color: var(--gray-100-white, #fff);
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
  white-space: nowrap;
}
.upsell-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--Gray-500, #949494);
  font-size: 22px;
  font-weight: 400;
  line-height: 1;
}
.upsell-fallback {
  color: var(--orange-300-main, #eb4c10);
}

.paywall_use_button_confirm {
  width: 100%;
  display: flex;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  border: none;
  background: var(--orange-300-main, #eb4c10);
  cursor: pointer;
  color: var(--gray-100-white, #fff);
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;

  &.disabled {
    background: var(--Gray-500, #949494);
    cursor: not-allowed;
  }
}
</style>
